// Финансовая страница Stage 2: счета и платежи.
// Регистрация счёта со строками — атомарная команда; подтверждение платежа —
// команда с revision и Idempotency-Key. Доступ ограничивается backend (finance
// app: accountant/supervisor/admin).
import { useState } from 'react';
import { finance } from '../api/endpoints';
import type {
    Invoice, InvoiceLine, OwnLegalEntity, Party, Payment, PaymentAllocation,
} from '../api/types';
import {
    Card, Column, DataTable, Empty, ErrorBox, Field, fmtDate, fmtMoney,
    PageHeader, shortId, StatusBadge,
} from '../components/ui';
import { useAsync } from '../hooks';
import { BankOperationsTab } from './finance/BankOperationsTab';
// === WT4 finance (§13): вкладка «Требуют внимания» ===
import { FinanceAttentionTab } from './finance/FinanceAttentionTab';

const CURRENCIES = ['RUB', 'USD', 'EUR', 'CNY'];

interface InvoiceLineDraft {
    key: number;
    itemId: string;
    amount: string;
    description: string;
    offerLineId: string;
}

let lineSeq = 0;
const newLine = (): InvoiceLineDraft => ({ key: ++lineSeq, itemId: '', amount: '', description: '', offerLineId: '' });

function RegisterInvoiceForm({ entities, payerParties, onDone }: {
    entities: OwnLegalEntity[]; payerParties: Array<Pick<Party, 'id' | 'name'>>; onDone: () => void;
}) {
    const [issuerId, setIssuerId] = useState('');
    const [payerId, setPayerId] = useState('');
    const [number, setNumber] = useState('');
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [currency, setCurrency] = useState('RUB');
    const [lines, setLines] = useState<InvoiceLineDraft[]>([newLine()]);
    const [err, setErr] = useState<unknown>(null);
    const [busy, setBusy] = useState(false);

    const total = lines.reduce((acc, l) => acc + (parseFloat(l.amount) || 0), 0);

    const updateLine = (key: number, patch: Partial<InvoiceLineDraft>) =>
        setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

    const submit = async () => {
        setBusy(true); setErr(null);
        try {
            await finance.registerInvoice({
                issuer_id: issuerId,
                payer_id: payerId || null,
                number, date, currency,
                total: total.toFixed(2),
                status: 'issued',
                lines: lines.map((l) => ({
                    item_id: l.itemId, amount: l.amount, description: l.description,
                    offer_line_id: l.offerLineId || null,
                })),
            });
            onDone();
        } catch (x) { setErr(x); } finally { setBusy(false); }
    };

    const valid = issuerId && number && lines.every((l) => l.itemId && l.amount);

    return (
        <Card title="Регистрация счёта">
            <ErrorBox error={err} />
            <div className="form-grid">
                <Field label="Эмитент (юрлицо REGWAY)">
                    <select className="input" value={issuerId} onChange={(e) => setIssuerId(e.target.value)}>
                        <option value="">— выберите —</option>
                        {entities.map((en) => <option key={en.id} value={en.id}>{shortId(en.id)}</option>)}
                    </select>
                </Field>
                <Field label="Плательщик">
                    <select className="input" value={payerId} onChange={(e) => setPayerId(e.target.value)}>
                        <option value="">— не указан —</option>
                        {payerParties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                </Field>
                <Field label="Номер счёта">
                    <input className="input" value={number} onChange={(e) => setNumber(e.target.value)} />
                </Field>
                <Field label="Дата">
                    <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                </Field>
                <Field label="Валюта">
                    <select className="input" value={currency} onChange={(e) => setCurrency(e.target.value)}>
                        {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                </Field>
            </div>

            <div className="section">
                <h3>Строки счёта</h3>
                {lines.map((l, idx) => (
                    <div className="form-actions" key={l.key}>
                        <Field label={`Позиция заявки (ID) #${idx + 1}`}>
                            <input className="input" style={{ width: 320 }} value={l.itemId}
                                   placeholder="UUID позиции"
                                   onChange={(e) => updateLine(l.key, { itemId: e.target.value })} />
                        </Field>
                        <Field label="Сумма">
                            <input className="input" style={{ width: 120 }} value={l.amount}
                                   onChange={(e) => updateLine(l.key, { amount: e.target.value })} />
                        </Field>
                        <Field label="Описание">
                            <input className="input" value={l.description}
                                   onChange={(e) => updateLine(l.key, { description: e.target.value })} />
                        </Field>
                        <Field label="Строка КП (ID, опц.)">
                            <input className="input" style={{ width: 200 }} value={l.offerLineId}
                                   placeholder="UUID строки предложения"
                                   onChange={(e) => updateLine(l.key, { offerLineId: e.target.value })} />
                        </Field>
                        {lines.length > 1 ? (
                            <button className="btn small danger" onClick={() => setLines((p) => p.filter((x) => x.key !== l.key))}>×</button>
                        ) : null}
                    </div>
                ))}
                <button className="btn small" onClick={() => setLines((p) => [...p, newLine()])}>+ строка</button>
            </div>

            <div className="form-actions" style={{ marginTop: 12 }}>
                <strong>Итого: {fmtMoney(total.toFixed(2), currency)}</strong>
                <button className="btn primary" disabled={busy || !valid} onClick={() => void submit()}>
                    Зарегистрировать счёт
                </button>
            </div>
        </Card>
    );
}

function PaymentCard({ payment, invoiceLines, onChanged }: {
    payment: Payment; invoiceLines: InvoiceLine[]; onChanged: () => void;
}) {
    const [err, setErr] = useState<unknown>(null);
    const [busy, setBusy] = useState(false);
    const [allocLine, setAllocLine] = useState('');
    const [allocAmount, setAllocAmount] = useState('');

    const run = async (fn: () => Promise<unknown>) => {
        setBusy(true); setErr(null);
        try { await fn(); onChanged(); } catch (x) { setErr(x); } finally { setBusy(false); }
    };

    return (
        <Card title={`Платёж ${payment.external_id || shortId(payment.id)}`}
              actions={<StatusBadge value={payment.status} />}>
            <div className="kv-grid" style={{ marginBottom: 8 }}>
                <div className="kv"><span className="kv-key">Сумма</span><span className="kv-value">{fmtMoney(payment.amount, payment.currency)}</span></div>
                <div className="kv"><span className="kv-key">Дата</span><span className="kv-value">{fmtDate(payment.date)}</span></div>
                <div className="kv"><span className="kv-key">Подтверждён</span><span className="kv-value">{fmtDate(payment.confirmed_at)}</span></div>
                <div className="kv"><span className="kv-key">Revision</span><span className="kv-value">{payment.revision ?? '—'}</span></div>
            </div>
            <ErrorBox error={err} />
            <div className="form-actions">
                {payment.status === 'pending' ? (
                    <button className="btn small primary" disabled={busy}
                            onClick={() => void run(() => finance.confirmPayment(payment.id, payment.revision))}>
                        Подтвердить платёж
                    </button>
                ) : null}
                {payment.status === 'confirmed' ? (
                    <>
                        <Field label="Строка счёта">
                            <select className="input" value={allocLine} onChange={(e) => setAllocLine(e.target.value)}>
                                <option value="">— выберите —</option>
                                {invoiceLines.map((l) => (
                                    <option key={l.id} value={l.id}>
                                        {shortId(l.invoice)} · {l.description || shortId(l.item)} · {fmtMoney(l.amount)}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Сумма распределения">
                            <input className="input" style={{ width: 120 }} value={allocAmount}
                                   onChange={(e) => setAllocAmount(e.target.value)} />
                        </Field>
                        <button className="btn small" disabled={busy || !allocLine || !allocAmount}
                                onClick={() => void run(() => finance.allocate(
                                    payment.id, payment.revision,
                                    [{ invoice_line_id: allocLine, amount: allocAmount }]))}>
                            Распределить
                        </button>
                    </>
                ) : null}
            </div>
        </Card>
    );
}

export function FinancePage() {
    const [tab, setTab] = useState<'invoices' | 'payments' | 'bank-ops' | 'attention'>('invoices');
    const invoices = useAsync<Invoice[]>(() => finance.invoices(), []);
    const invoiceLines = useAsync<InvoiceLine[]>(() => finance.invoiceLines().catch(() => []), []);
    const payments = useAsync<Payment[]>(() => finance.payments(), []);
    const allocations = useAsync<PaymentAllocation[]>(() => finance.allocations().catch(() => []), []);
    const entities = useAsync<OwnLegalEntity[]>(() => finance.ownLegalEntities().catch(() => []), []);
    // У бухгалтера нет доступа к parties API — используем минимальный справочник.
    const payerParties = useAsync<Array<Pick<Party, 'id' | 'name'>>>(
        () => finance.payerChoices().catch(() => []), []);

    const [showForm, setShowForm] = useState(false);
    const [payAmount, setPayAmount] = useState('');
    const [payCurrency, setPayCurrency] = useState('RUB');
    const [payExternalId, setPayExternalId] = useState('');
    const [payRecipient, setPayRecipient] = useState('');
    const [payPayer, setPayPayer] = useState('');
    const [payErr, setPayErr] = useState<unknown>(null);
    const [busy, setBusy] = useState(false);

    const reload = () => { invoices.reload(); invoiceLines.reload(); payments.reload(); allocations.reload(); };

    const createPayment = async () => {
        setBusy(true); setPayErr(null);
        try {
            await finance.createPayment({
                amount: payAmount, currency: payCurrency,
                external_id: payExternalId || undefined,
                recipient_entity: payRecipient || undefined,
                payer: payPayer || undefined,
                date: new Date().toISOString().slice(0, 10),
            });
            setPayAmount(''); setPayExternalId(''); setPayPayer('');
            payments.reload();
        } catch (x) { setPayErr(x); } finally { setBusy(false); }
    };

    const invoiceCols: Column<Invoice>[] = [
        { key: 'number', title: 'Номер', render: (r) => r.number ?? shortId(r.id) },
        { key: 'date', title: 'Дата', render: (r) => fmtDate(r.date) },
        { key: 'total', title: 'Сумма', render: (r) => fmtMoney(r.total, r.currency) },
        { key: 'status', title: 'Статус', render: (r) => <StatusBadge value={r.status} /> },
        { key: 'payer', title: 'Плательщик', render: (r) => (r.payer ? shortId(r.payer) : '—') },
    ];

    const allocCols: Column<PaymentAllocation>[] = [
        { key: 'payment', title: 'Платёж', render: (r) => shortId(r.payment) },
        { key: 'invoice_line', title: 'Строка счёта', render: (r) => shortId(r.invoice_line) },
        { key: 'amount', title: 'Сумма', render: (r) => fmtMoney(r.amount) },
    ];

    return (
        <div>
            <PageHeader title="Финансы">
                <button className={`btn small ${tab === 'invoices' ? 'primary' : ''}`} onClick={() => setTab('invoices')}>Счета</button>
                <button className={`btn small ${tab === 'payments' ? 'primary' : ''}`} onClick={() => setTab('payments')}>Платежи</button>
                <button className={`btn small ${tab === 'bank-ops' ? 'primary' : ''}`} onClick={() => setTab('bank-ops')}>Банковские операции</button>
                {/* === WT4 finance (§13) === */}
                <button className={`btn small ${tab === 'attention' ? 'primary' : ''}`} onClick={() => setTab('attention')}>Требуют внимания</button>
                <button className="btn" onClick={reload}>Обновить</button>
            </PageHeader>

            {tab === 'attention' ? (
                <FinanceAttentionTab />
            ) : tab === 'bank-ops' ? (
                <BankOperationsTab />
            ) : tab === 'invoices' ? (
                <>
                    <Card title="Счета" actions={
                        <button className="btn small primary" onClick={() => setShowForm((v) => !v)}>
                            {showForm ? 'Скрыть форму' : 'Новый счёт'}
                        </button>
                    }>
                        <ErrorBox error={invoices.error} onRetry={invoices.reload} />
                        <DataTable columns={invoiceCols} rows={invoices.data ?? []} empty="Счетов нет" />
                    </Card>
                    {showForm ? (
                        <RegisterInvoiceForm
                            entities={entities.data ?? []}
                            payerParties={payerParties.data ?? []}
                            onDone={() => { setShowForm(false); invoices.reload(); invoiceLines.reload(); }}
                        />
                    ) : null}
                    <Card title="Строки счетов">
                        <ErrorBox error={invoiceLines.error} />
                        {(invoiceLines.data ?? []).length === 0
                            ? <Empty text="Строк нет" />
                            : (
                                <DataTable
                                    columns={[
                                        { key: 'invoice', title: 'Счёт', render: (r: InvoiceLine) => shortId(r.invoice) },
                                        { key: 'item', title: 'Позиция', render: (r: InvoiceLine) => (r.item ? shortId(r.item) : '—') },
                                        { key: 'amount', title: 'Сумма', render: (r: InvoiceLine) => fmtMoney(r.amount) },
                                        { key: 'description', title: 'Описание', render: (r: InvoiceLine) => r.description || '—' },
                                    ]}
                                    rows={invoiceLines.data ?? []} />
                            )}
                    </Card>
                </>
            ) : (
                <>
                    <Card title="Регистрация платежа">
                        <ErrorBox error={payErr} />
                        <div className="form-actions">
                            <Field label="Сумма">
                                <input className="input" style={{ width: 140 }} value={payAmount}
                                       onChange={(e) => setPayAmount(e.target.value)} />
                            </Field>
                            <Field label="Валюта">
                                <select className="input" value={payCurrency} onChange={(e) => setPayCurrency(e.target.value)}>
                                    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </Field>
                            <Field label="Внешний ID (опционально)">
                                <input className="input" value={payExternalId}
                                       onChange={(e) => setPayExternalId(e.target.value)} />
                            </Field>
                            <Field label="Получатель (юрлицо REGWAY)">
                                <select className="input" value={payRecipient} onChange={(e) => setPayRecipient(e.target.value)}>
                                    <option value="">— выберите —</option>
                                    {(entities.data ?? []).map((en) => (
                                        <option key={en.id} value={en.id}>{shortId(en.id)} · {en.base_currency}</option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="Плательщик">
                                <select className="input" value={payPayer} onChange={(e) => setPayPayer(e.target.value)}>
                                    <option value="">— не указан —</option>
                                    {(payerParties.data ?? []).map((p) => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                </select>
                            </Field>
                            <button className="btn primary" disabled={busy || !payAmount || !payRecipient} onClick={() => void createPayment()}>
                                Зарегистрировать
                            </button>
                        </div>
                    </Card>
                    <ErrorBox error={payments.error} onRetry={payments.reload} />
                    {(payments.data ?? []).length === 0 ? <Empty text="Платежей нет" /> : null}
                    {(payments.data ?? []).map((p) => (
                        <PaymentCard key={p.id} payment={p}
                                     invoiceLines={invoiceLines.data ?? []}
                                     onChanged={reload} />
                    ))}
                    <Card title="Распределения">
                        <ErrorBox error={allocations.error} />
                        <DataTable columns={allocCols} rows={allocations.data ?? []} empty="Распределений нет" />
                    </Card>
                </>
            )}
        </div>
    );
}
